'use client'

import { Plus } from "lucide-react"
import { formatImageURL, formatPrice, getBuyXTriggerCount, getBuyXGetYRewardRule } from "@/utils/util"
import { useCart } from "@/lib/commerce/hooks"
import { panelFocusRing } from "../elements/SidePanel"

const eyebrow = 'text-[11px] tracking-[0.14em] uppercase text-muted-foreground'

export function RewardRow({ reward, rewardPrice, label, disabled, loading, onAdd }: { reward: any, rewardPrice: number, label: string, disabled: boolean, loading: boolean, onAdd: () => void }) {
  return (
    <div className="flex items-center gap-4 py-4">
      <img src={formatImageURL(reward.image || 'placeholder.png')} alt={reward.name} className="w-14 aspect-3/4 object-cover bg-muted shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm line-clamp-1">{reward.name}</div>
        <div className="mt-1 flex items-center gap-2 text-[13px] tabular-nums">
          <span>{rewardPrice === 0 ? 'Үнэгүй' : formatPrice(rewardPrice)}</span>
          {reward.price && rewardPrice < reward.price && <span className="line-through text-muted-foreground">{formatPrice(reward.price)}</span>}
          {reward.valueType === 'percent' && reward.valuePercent && <span className="text-muted-foreground">-{reward.valuePercent}%</span>}
        </div>
      </div>
      <button type="button" onClick={onAdd} disabled={disabled} className={`shrink-0 inline-flex items-center gap-1.5 h-9 px-4 border border-foreground text-xs tracking-wide transition-colors duration-150 hover:bg-foreground hover:text-background disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-foreground ${panelFocusRing}`}>
        {label === 'Нэмэх' && !loading && <Plus aria-hidden className="size-3" strokeWidth={1.5} />}
        {loading && label === 'Нэмэх' ? 'Нэмж байна…' : label}
      </button>
    </div>
  )
}

function RewardSection({ title, rule, note, className, children }: { title: string, rule: string, note?: string, className: string, children: React.ReactNode }) {
  return (
    <section className={`border border-border p-5 ${className}`}>
      <div className={eyebrow}>{title}</div>
      <p className="mt-2 text-sm">{rule}</p>
      {note && <p className="mt-1 text-xs text-muted-foreground">{note}</p>}
      <div className="mt-3 divide-y divide-border">{children}</div>
    </section>
  )
}

export function BuyXGetYOffers({ className = '' }: { className?: string }) {
  const { buyXGetYInfo, getBuyXGetYRewardState, addRewardItem, rewardLoading } = useCart()

  return (
    <>
      {buyXGetYInfo?.map((info: any) => {
        if (!info.qualifies || !info.rewardProducts?.length) return null
        const { totalRemaining } = getBuyXGetYRewardState(info, info.rewardProducts[0])
        if (totalRemaining <= 0) return null
        const triggerCount = getBuyXTriggerCount(info.discount)
        return (
          <RewardSection key={info.discount.id} className={className} title={info.discount?.name || 'Хөнгөлөлт'} rule={`${triggerCount} авбал ${getBuyXGetYRewardRule(info)}`} note={`Нөхцөл хангагдлаа (${info.triggerCount}/${triggerCount}) — ${totalRemaining} урамшуулалтай бараа авах боломжтой`}>
            {info.rewardProducts.map((reward: any) => {
              const { canAdd, isLockedByOther, isOutOfStock, rewardPrice } = getBuyXGetYRewardState(info, reward)
              if (isLockedByOther) return null
              return <RewardRow key={reward.id} reward={reward} rewardPrice={rewardPrice} label={isOutOfStock ? 'Дууссан' : canAdd ? 'Нэмэх' : 'Нэмэгдсэн'} disabled={!canAdd || rewardLoading} loading={rewardLoading} onAdd={() => addRewardItem({ ...reward, discountId: info.discount.id, discountName: info.discount.name, discountBase: info.discount.discountBase, variant: reward.variant || { id: reward.defaultVariantId, price: reward.price, priceSale: reward.priceSale, stock: reward.stock } }, 'buyXgetY')} />
            })}
          </RewardSection>
        )
      })}
    </>
  )
}

export function OrderRewardOffers({ source = 'order', className = '' }: { source?: 'order' | 'coupon', className?: string }) {
  const { availableOrderRewardItems, availableCouponRewardItems, activeOrderDiscount, appliedCoupon, addRewardItem, rewardLoading, getRewardPrice, isRewardOutOfStock } = useCart()
  const items = source === 'coupon' ? availableCouponRewardItems : availableOrderRewardItems
  if (!items?.length) return null

  const title = source === 'coupon' ? appliedCoupon?.discount?.name || appliedCoupon?.name || 'Захиалгын бэлэг' : activeOrderDiscount?.name || 'Захиалгын бэлэг'
  const rule = source === 'coupon' ? 'Хөнгөлөлтийн кодын бэлэг' : 'Захиалгын хөнгөлөлтийн бэлэг'

  return (
    <RewardSection className={className} title={title} rule={rule}>
      {items.map((reward: any) => {
        const isOutOfStock = isRewardOutOfStock(reward)
        return <RewardRow key={reward.id} reward={reward} rewardPrice={getRewardPrice(reward)} label={isOutOfStock ? 'Дууссан' : 'Нэмэх'} disabled={isOutOfStock || rewardLoading} loading={rewardLoading} onAdd={() => addRewardItem(reward, 'order')} />
      })}
    </RewardSection>
  )
}
